import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
} from '@mui/material';
import { Add, Remove, Flight, CalendarToday, Star, People, Mosque, Public, Landscape, Weekend } from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  PRODUCT_LINES,
  resolveProductLine,
  getProductLineDefaults,
  coerceBookingLocations,
  getCountriesForProductLine,
  getDepartureCities,
  cityToAirportCode,
} from '../../../utils/productLineConfig';

const productLineIcons = {
  Umrah: <Mosque />,
  International: <Public />,
  Domestic: <Landscape />,
  Local: <Weekend />,
};

const BasicInformationStepV2 = ({ bookingData, updateBookingData }) => {
  const selectedLine = resolveProductLine(bookingData);
  const countries = getCountriesForProductLine(selectedLine);
  const cities = getDepartureCities(selectedLine);

  const packageTypes = [
    { value: '5 Star', label: '5 Star - Luxury', icon: <Star />, color: '#FFD700' },
    { value: '4 Star', label: '4 Star - Premium', icon: <Star />, color: '#C0C0C0' },
    { value: '3 Star', label: '3 Star - Standard', icon: <Star />, color: '#CD7F32' },
    { value: 'Economy', label: 'Economy', icon: <Star />, color: '#808080' },
  ];

  const applyProductLine = (value) => {
    const defaults = getProductLineDefaults(value);
    updateBookingData({
      ...defaults,
      ...coerceBookingLocations(value, defaults),
    });
  };

  const updateTravelers = (type, delta) => {
    const currentValue = bookingData[type] || 0;
    const newValue = Math.max(0, currentValue + delta);
    if (type === 'adults' && newValue === 0) return; // At least 1 adult required
    updateBookingData({ [type]: newValue });
  };

  const getTotalTravelers = () => {
    return (bookingData.adults || 0) + (bookingData.children || 0) + (bookingData.infants || 0);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Flight color="primary" />
        Basic Travel Information
      </Typography>

      <Grid container spacing={3}>
        {/* Journey type */}
        <Grid item xs={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              What kind of trip?
            </Typography>
            <Grid container spacing={2}>
              {PRODUCT_LINES.map((line) => {
                const selected = selectedLine === line.value;
                return (
                  <Grid item xs={12} sm={6} md={3} key={line.value}>
                    <Card
                      variant="outlined"
                      onClick={() => applyProductLine(line.value)}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 2,
                        height: '100%',
                        border: selected ? '2px solid' : '1px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        transition: 'all 0.25s',
                        '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
                      }}
                    >
                      <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <Box sx={{ color: 'primary.main', display: 'flex', mt: 0.25 }}>
                          {productLineIcons[line.value]}
                        </Box>
                        <Box>
                          <Typography variant="body1" fontWeight={700}>{line.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{line.detail}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </motion.div>
        </Grid>

        {/* Destination country (filters cities & flights below) */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
          >
            <Card variant="outlined" sx={{ borderRadius: 2, '&:hover': { boxShadow: 3 } }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  {selectedLine === 'International' ? 'Destination country' : 'Trip region'}
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={bookingData.destinationCountry || countries[0]?.code}
                    onChange={(e) => {
                      const country = e.target.value;
                      updateBookingData(
                        coerceBookingLocations(selectedLine, {
                          ...bookingData,
                          destinationCountry: country,
                        })
                      );
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    {countries.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Stay cities and flight destinations update based on this selection.
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Departure Date */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card variant="outlined" sx={{ borderRadius: 2, '&:hover': { boxShadow: 3 } }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CalendarToday color="primary" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Departure Date
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type="date"
                  value={bookingData.departureDate}
                  onChange={(e) => updateBookingData({ departureDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: new Date().toISOString().split('T')[0],
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Departure City */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card variant="outlined" sx={{ borderRadius: 2, '&:hover': { boxShadow: 3 } }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Flight color="primary" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Departure City
                  </Typography>
                </Box>
                <FormControl fullWidth>
                  <Select
                    value={bookingData.flyFrom || cities[0]}
                    onChange={(e) => {
                      const flyFrom = e.target.value;
                      updateBookingData({
                        flyFrom,
                        flightOrigin: cityToAirportCode(flyFrom),
                      });
                    }}
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    {cities.map((city) => (
                      <MenuItem key={city} value={city}>
                        {city}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Package Type */}
        <Grid item xs={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Star color="primary" />
              Select Package Type
            </Typography>
            <Grid container spacing={2}>
              {packageTypes.map((pkg) => (
                <Grid item xs={12} sm={6} md={3} key={pkg.value}>
                  <Card
                    variant="outlined"
                    onClick={() => updateBookingData({ packageType: pkg.value })}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 2,
                      border: bookingData.packageType === pkg.value ? '2px solid' : '1px solid',
                      borderColor: bookingData.packageType === pkg.value ? 'primary.main' : 'divider',
                      transition: 'all 0.3s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 3,
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box sx={{ fontSize: 40, color: pkg.color, mb: 1 }}>
                        {pkg.icon}
                      </Box>
                      <Typography variant="body1" fontWeight={600}>
                        {pkg.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Grid>

        {/* Travelers */}
        <Grid item xs={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <People color="primary" />
                    Number of Travelers
                  </Typography>
                  <Chip
                    label={`Total: ${getTotalTravelers()}`}
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                <Grid container spacing={2}>
                  {/* Adults */}
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(0, 59, 149, 0.05)',
                      }}
                    >
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          Adults
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Age 12+
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => updateTravelers('adults', -1)}
                          disabled={bookingData.adults <= 1}
                          data-testid="remove-adults"
                          sx={{
                            backgroundColor: 'white',
                            '&:hover': { backgroundColor: 'grey.100' },
                          }}
                        >
                          <Remove />
                        </IconButton>
                        <Typography variant="h6" sx={{ minWidth: 40, textAlign: 'center' }}>
                          {bookingData.adults}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => updateTravelers('adults', 1)}
                          data-testid="add-adults"
                          sx={{
                            backgroundColor: 'white',
                            '&:hover': { backgroundColor: 'grey.100' },
                          }}
                        >
                          <Add />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Children */}
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(2, 136, 209, 0.05)',
                      }}
                    >
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          Children
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Age 2-11
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => updateTravelers('children', -1)}
                          disabled={bookingData.children <= 0}
                          data-testid="remove-children"
                          sx={{
                            backgroundColor: 'white',
                            '&:hover': { backgroundColor: 'grey.100' },
                          }}
                        >
                          <Remove />
                        </IconButton>
                        <Typography variant="h6" sx={{ minWidth: 40, textAlign: 'center' }}>
                          {bookingData.children}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => updateTravelers('children', 1)}
                          data-testid="add-children"
                          sx={{
                            backgroundColor: 'white',
                            '&:hover': { backgroundColor: 'grey.100' },
                          }}
                        >
                          <Add />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Infants */}
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(156, 39, 176, 0.05)',
                      }}
                    >
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          Infants
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Under 2
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => updateTravelers('infants', -1)}
                          disabled={bookingData.infants <= 0}
                          data-testid="remove-infants"
                          sx={{
                            backgroundColor: 'white',
                            '&:hover': { backgroundColor: 'grey.100' },
                          }}
                        >
                          <Remove />
                        </IconButton>
                        <Typography variant="h6" sx={{ minWidth: 40, textAlign: 'center' }}>
                          {bookingData.infants}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => updateTravelers('infants', 1)}
                          data-testid="add-infants"
                          sx={{
                            backgroundColor: 'white',
                            '&:hover': { backgroundColor: 'grey.100' },
                          }}
                        >
                          <Add />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BasicInformationStepV2;
